import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { NAV_HEIGHT } from '~/common/constants/page';
import { DeleteRoleModal } from '~/components/Roles/DeleteRoleModal';
import { RoleFormPanel } from '~/components/Roles/RoleFormPanel';
import { trackSegment } from '~/components/SegmentAnalytics';
import { PersistedTable } from '~/components/Shared/PersistedTable/PersistedTable';
import { User } from '~/components/Shared/User/User';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { Select } from '~/eds/src/components/Select/Select';
import { Text } from '~/eds/src/components/Text/Text';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { RoleType } from '~/enums/Roles';
import { TableContextType } from '~/enums/TableContextType';
import { RoleDetails } from '~/features/roles/RoleDetails';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useTableSettings } from '~/hooks/useTableSettings';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { api } from '~/redux/api/createApi';
import { RoleAttributes } from '~/redux/api/methods/roles/roles';
import { RoutePathType } from '~/routing/enums';
import { generateNav } from '~/routing/nav';
import { useRouting } from '~/routing/useRouting';
import { Nullable } from '~/types/Nullable';

const COLUMNS = [
  'name',
  'role_type',
  'description',
  'users',
  'created_by',
  'created_on',
];

const Page = () => {
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const [search, setSearch] = useState<Nullable<string>>('');
  const [roleType, setRoleType] = useState<Nullable<string>>('');
  const [showRolePanel, setShowRolePanel] = useState(false);
  const [roleDetails, setRoleDetails] = useState<RoleAttributes | undefined>(
    undefined,
  );
  const [showDuplicateRolePanel, setShowDuplicateRolePanel] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRole, setSelectedRole] =
    useState<Nullable<RoleAttributes>>(null);
  const user = useCurrentUser();
  const hasPremiumPermissions = useFlag(FlagType.PremiumPermissions);
  const { params } = useRouting();
  const { clientId } = params as { clientId: string };

  const nav = generateNav({
    current: {
      text: intl.formatMessage({
        id: 'RolesPage.rolesNavText',
        defaultMessage: 'Roles',
        description: 'Navigation text for roles page',
      }),
      pathname: RoutePathType.AdminRoles,
    },
    from: RoutePathType.AdminConsoleClient,
    params: {
      clientId: clientId ?? user.client,
    },
    intl,
  });

  const {
    tableSettings = {
      pageSize: 10,
      columnOrder: COLUMNS,
    },
  } = useTableSettings(TableContextType.CustomRoles);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(tableSettings.pageSize);
  const [getRoles, { isFetching, data }] =
    api.endpoints.getRoles.useLazyQuery();

  useEffect(() => {
    getRoles({
      client_id: clientId ?? user.client,
      page,
      pageSize,
      name: search || undefined,
      role_type: roleType || undefined,
    });
  }, [page, pageSize, roleType]);

  const columns = useMemo(
    () => [
      {
        key: 'name',
        title: intl.formatMessage({
          id: 'RolesPage.nameColumnTitle',
          defaultMessage: 'Name',
          description: 'Column title for role name',
        }),
        disableSortBy: true,
        mapCellProps: (role: RoleAttributes) => ({
          text: role.name,
        }),
      },
      {
        key: 'role_type',
        title: intl.formatMessage({
          id: 'RolesPage.roleTypeColumnTitle',
          defaultMessage: 'Role type',
          description: 'Column title for role type',
        }),
        disableSortBy: true,
        mapCellProps: (role: RoleAttributes) => ({
          text: role.role_type,
        }),
      },
      {
        key: 'description',
        title: intl.formatMessage({
          id: 'RolesPage.roleDescriptionColumnTitle',
          defaultMessage: 'Role description',
          description: 'Column title for role description',
        }),
        disableSortBy: true,
        mapCellProps: (role: RoleAttributes) => ({
          text: role.description,
        }),
      },
      {
        key: 'users',
        title: intl.formatMessage({
          id: 'RolesPage.usersColumnTitle',
          defaultMessage: 'Users',
          description: 'Column title for users',
        }),
        cellType: 'link',
        disableSortBy: true,
        mapCellProps: (role: RoleAttributes) => ({
          text: formatNumber(role.users),
          pathname: `/admin/${clientId}/users/`,
          search: `role=${role.id}`,
        }),
      },
      {
        key: 'created_by',
        title: intl.formatMessage({
          id: 'RolesPage.createdByColumnTitle',
          defaultMessage: 'Created by',
          description: 'Column title for created by',
        }),
        disableSortBy: true,
        renderCell: (role: RoleAttributes) => {
          return (
            <User
              mode="avatar-name"
              id={role.created_by}
              preset={!role.created_by ? 'evisort' : undefined}
            />
          );
        },
      },
      {
        key: 'created_at',
        title: intl.formatMessage({
          id: 'RolesPage.createdOnColumnTitle',
          defaultMessage: 'Created on',
          description: 'Column title for created on date',
        }),
        disableSortBy: true,
        cellType: 'datetime',
        mapCellProps: ({ created_at }: RoleAttributes): Object => ({
          datetime: created_at,
        }),
      },
    ],
    [],
  );

  const getRowActions = (row: RoleAttributes) => [
    {
      label: intl.formatMessage({
        id: 'RolesPage.editRoleAction',
        defaultMessage: 'Edit role',
        description: 'Row action to edit a role',
      }),
      onClick: async (role: RoleAttributes) => {
        setShowRolePanel(true);
        setSelectedRole(role);
        trackSegment('openRoleFormPanel', {
          user_id: user.id,
          client_id: clientId ?? user.client,
          role_id: role.id,
          role_name: role.name,
        });
      },
      disabled: row.role_type === RoleType.PreBuiltRole,
    },
    {
      condition: () => hasPremiumPermissions,
      label: intl.formatMessage({
        id: 'RolesPage.duplicateRoleAction',
        defaultMessage: 'Duplicate role',
        description: 'Row action to duplicate a role',
      }),
      onClick: async (role: RoleAttributes) => {
        setShowDuplicateRolePanel(true);
        setShowRolePanel(true);
        setSelectedRole(role);
        trackSegment('openDuplicateRolePanel', {
          user_id: user.id,
          client_id: clientId ?? user.client,
          role_id: role.id,
          role_name: role.name,
        });
      },
      disabled:
        row.role_type === RoleType.PreBuiltRole &&
        (row.name === 'Owner' || !hasPremiumPermissions),
    },
    {
      label: intl.formatMessage({
        id: 'RolesPage.deleteRoleAction',
        defaultMessage: 'Delete role',
        description: 'Row action to delete a role',
      }),
      onClick: async (role: RoleAttributes) => {
        trackSegment('openDeleteRoleModal', {
          user_id: user.id,
          client_id: clientId ?? user.client,
          role_id: role.id,
          role_name: role.name,
        });
        setSelectedRole(role);
        setShowDeleteModal(true);
      },
      disabled: row.role_type === RoleType.PreBuiltRole,
    },
  ];

  const onPaginate = useCallback(
    ({ pageIndex }: { pageIndex: number }) => {
      if (pageIndex !== page) {
        setPage(pageIndex);
      }
    },
    [page],
  );
  const onPageSizeChange = useCallback((pageSize: number) => {
    setPage(1);
    setPageSize(pageSize);
  }, []);

  const panel = roleDetails
    ? {
        title: roleDetails.name,
        width: 'm' as const,
        chips: [
          {
            text: roleDetails.role_type,
            status: 'info' as const,
          },
        ],
        hidden: {
          isHidden: false,
          onHide: () => setRoleDetails(undefined),
        },
        children: <RoleDetails role={roleDetails} />,
      }
    : undefined;

  return (
    <PageLayout
      title={intl.formatMessage({
        id: 'RolesPage.pageTitle',
        defaultMessage: 'Roles',
        description: 'Page title for roles management',
      })}
      nav={nav}
      description={intl.formatMessage({
        id: 'RolesPage.pageDescription',
        defaultMessage:
          'Manage and create custom roles with specific permissions to control what your users can do.',
        description: 'Page description for roles management',
      })}
      panel={panel}
      actions={[
        {
          level: 'primary',
          text: intl.formatMessage({
            id: 'RolesPage.newRoleButton',
            defaultMessage: 'New Role',
            description: 'Button text to create a new role',
          }),
          onClick: () => {
            setShowRolePanel(true);
            trackSegment('openRoleFormPanel', {
              user_id: user.id,
              client_id: clientId ?? user.client,
            });
          },
        },
      ]}
      loadingContent={{
        isLoading: isFetching,
        message: intl.formatMessage({
          id: 'RolesPage.loadingRolesMessage',
          defaultMessage: 'Loading roles…',
          description: 'Loading message while roles are being fetched',
        }),
      }}
    >
      <Layout justify="space-between">
        <Layout flex="1" mr={4}>
          <form
            style={{ width: '100%' }}
            onSubmit={(e) => {
              e.preventDefault();
              trackSegment('searchTextRoles', {
                search,
                user_id: user.id,
                client_id: clientId ?? user.client,
              });
              setPage(1);
              getRoles({
                page: 1,
                pageSize,
                name: search || undefined,
                client_id: clientId ?? user.client,
                role_type: roleType || undefined,
              });
            }}
          >
            <SearchInput
              width="100%"
              name="search"
              value={search}
              onChange={setSearch}
              placeholder={intl.formatMessage({
                id: 'RolesPage.searchPlaceholder',
                defaultMessage: 'Search by role name',
                description: 'Placeholder text for role search input',
              })}
            />
          </form>
        </Layout>
        <Layout align="center" spacing={1}>
          <Text>
            {intl.formatMessage({
              id: 'RolesPage.filtersLabel',
              defaultMessage: 'Filters',
              description: 'Label for filters section',
            })}
          </Text>
          <Select
            width="input.m.width"
            options={Object.values(RoleType).map((type) => ({
              label: type,
              value: type,
            }))}
            name="roleType"
            placeholder={intl.formatMessage({
              id: 'RolesPage.roleTypePlaceholder',
              defaultMessage: 'Role Type',
              description: 'Placeholder for role type filter',
            })}
            value={roleType}
            onChange={(value) => {
              trackSegment('selectRoleType', {
                name: value,
                user_id: user.id,
                client_id: clientId ?? user.client,
              });
              setRoleType(value);
            }}
            isMulti={false}
          />
        </Layout>
      </Layout>
      <Layout direction="column" spacing={8} mt={6}>
        {data?.results?.length === 0 && search ? (
          <Layout align="center" justify="center">
            <Text>
              {intl.formatMessage(
                {
                  id: 'RolesPage.noResultsText',
                  defaultMessage: 'No results for: {searchTerm}',
                  description:
                    'Text when no search results are found. Example: searchTerm = "admin role"',
                },
                {
                  searchTerm: search,
                },
              )}
            </Text>
          </Layout>
        ) : (
          <PersistedTable
            name={TableContextType.CustomRoles}
            data={data?.results || []}
            options={{
              enableSelectRows: false,
              enablePageSizeSelect: true,
              enableStickyHeader: true,
              stickyHeaderOffset: NAV_HEIGHT,
            }}
            state={{
              pageIndex: page,
              pageSize,
            }}
            rowDetails={{
              onClick: setRoleDetails,
            }}
            getRowActions={getRowActions}
            columns={columns}
            context={TableContextType.CustomRoles}
            totalCount={data?.total}
            onPaginate={onPaginate}
            onPageSizeChange={onPageSizeChange}
          ></PersistedTable>
        )}
      </Layout>
      <RoleFormPanel
        visible={showRolePanel}
        isDuplicate={showDuplicateRolePanel}
        clientId={clientId ?? user.client}
        onHide={() => {
          setShowRolePanel(false);
          setShowDuplicateRolePanel(false);
          setSelectedRole(null);
        }}
        role={selectedRole}
      />
      {selectedRole && (
        <DeleteRoleModal
          isDefaultRole={selectedRole?.id === data?.samlDefaultRoleUuid}
          isVisible={showDeleteModal}
          role={selectedRole}
          clientId={clientId ?? user.client}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedRole(null);
          }}
        />
      )}
    </PageLayout>
  );
};

export default Page;
